import {
  Autocomplete,
  Chip,
  createFilterOptions,
  TextField,
  Typography,
} from "@mui/material"
import type { FC } from "react"

export interface MultiAutocompleteOption {
  id: string
  label: string
}

interface MultiAutocompleteProps {
  label: string
  options: MultiAutocompleteOption[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  onCreateNew?: () => void
}

const CREATE_SENTINEL = "__CREATE_NEW__"

type InternalOption = MultiAutocompleteOption & { isCreateNew?: boolean }

const baseFilter = createFilterOptions<InternalOption>()

export const MultiAutocomplete: FC<MultiAutocompleteProps> = ({
  label,
  options,
  selectedIds,
  onChange,
  onCreateNew,
}) => {
  const sorted = [...options].sort((a, b) => a.label.localeCompare(b.label))
  const selected = sorted.filter(o => selectedIds.includes(o.id))

  const filterOptions = (
    opts: InternalOption[],
    state: Parameters<typeof baseFilter>[1]
  ) => {
    const filtered = baseFilter(opts, state)
    if (onCreateNew) {
      filtered.push({
        id: CREATE_SENTINEL,
        label: "+ Create New",
        isCreateNew: true,
      })
    }
    return filtered
  }

  return (
    <Autocomplete
      multiple
      options={sorted as InternalOption[]}
      filterOptions={onCreateNew ? filterOptions : undefined}
      getOptionLabel={o => o.label}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={selected}
      onChange={(_, vals) => {
        if (vals.some((v: InternalOption) => v.isCreateNew)) {
          onCreateNew?.()
        } else {
          onChange(vals.map(v => v.id))
        }
      }}
      renderOption={
        onCreateNew
          ? (props, option: InternalOption) => (
              <li {...props} key={option.id}>
                {option.isCreateNew ? (
                  <Typography color="primary">{option.label}</Typography>
                ) : (
                  option.label
                )}
              </li>
            )
          : undefined
      }
      renderValue={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            label={option.label}
            size="small"
            {...getTagProps({ index })}
            key={option.id}
          />
        ))
      }
      renderInput={params => <TextField {...params} label={label} fullWidth />}
    />
  )
}
